import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { rootPath } from "@/novus/novus-paths"
import { displayValidationErrors } from "@/util/validations"
import { useMutation } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { z, ZodType } from "zod"

const UpdatePasswordMutation = gql(`
  mutation UpdateViewerPassword($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        ...Viewer
      }
      errors {
        path
        fullMessages
      }
    }
  }
`)

type FormValues = {
  currentPassword: string
  password: string
  passwordConfirmation: string
}

const schema: ZodType<FormValues> = z
  .object({
    currentPassword: z
      .string()
      .min(8, "A password of at least 8 characters is required"),
    password: z
      .string()
      .min(8, "A password of at least 8 characters is required"),
    passwordConfirmation: z
      .string()
      .min(8, "A password of at least 8 characters is required"),
  })
  .refine((values) => values.password === values.passwordConfirmation, {
    message: "Passwords do not match",
    path: ["passwordConfirmation"],
  })

const UpdatePasswordForm = () => {
  const navigate = useNavigate()
  const [mutate] = useMutation(UpdatePasswordMutation)
  const { toast } = useToast()

  const defaultValues: FormValues = {
    currentPassword: "",
    password: "",
    passwordConfirmation: "",
  }

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState, setError } = methods

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      const { data } = await mutate({
        variables: {
          input: {
            password: values.password,
            currentPassword: values.currentPassword,
          },
        },
      })

      if (data?.updateUser.errors && data?.updateUser.errors.length > 0) {
        throw data?.updateUser.errors
      }

      toast({
        title: "Password has been updated.",
      })
      navigate(rootPath.pattern)
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "user",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Password was not updated.",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup>
          <RhfField
            name="currentPassword"
            control={control}
            type="password"
            placeholder="Current password"
            label="Current Password"
            autoFocus={true}
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="password"
            control={control}
            type="password"
            placeholder="New password"
            label="New Password"
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="passwordConfirmation"
            control={control}
            type="password"
            placeholder="New password confirmation"
            label="New Password Confirmation"
          />
        </FieldGroup>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Save & Update
          </Button>
          <Link to={rootPath.pattern} className="text-neutral-600 text-sm">
            Close & Cancel
          </Link>
        </FormActions>
      </form>
    </FormProvider>
  )
}

export default UpdatePasswordForm
